<template>
  <div id="home">
    <div class="div" v-loading="loading">
      <div class="title">Card &amp; Autopay</div>

      <el-alert v-if="loadError" :title="loadError" type="error" :closable="false" show-icon></el-alert>

      <template v-if="info">
        <el-alert v-if="!info.allowed" type="info" :closable="false" show-icon
          title="Autopay is not available for your account. Please contact us if you'd like to use it."></el-alert>

        <!-- status -->
        <div class="status" :class="info.enabled ? 'on' : 'off'" v-if="info.allowed">
          <div>
            <b>Autopay is {{ info.enabled ? 'on' : 'off' }}</b>
            <p v-if="info.enabled && autopayCard">
              Each invoice is charged to {{ cardName(autopayCard) }} on its due date.
            </p>
            <p v-else>Invoices are not charged automatically. You pay them on the Bills page.</p>
          </div>
          <span v-if="info.enabled" class="btn gray" @click="turnOff" v-loading="saving">Turn off autopay</span>
        </div>

        <!-- saved cards -->
        <div class="section" v-if="info.allowed">
          <div class="subtitle">Saved cards</div>
          <el-empty v-if="info.cards.length == 0" :image-size="60" description="No saved card"></el-empty>
          <div class="cards" v-else>
            <label class="card" v-for="card in info.cards" :key="card.card_uuid"
              :class="{ selected: selectedCard == card.card_uuid, expired: card.expired }">
              <input type="radio" :value="card.card_uuid" v-model="selectedCard" :disabled="card.expired"/>
              <div class="cardText">
                <b>{{ cardName(card) }}</b>
                <span>Expires {{ pad(card.exp_month) }}/{{ card.exp_year }}</span>
                <el-tag v-if="card.expired" size="mini" type="danger">Expired</el-tag>
                <el-tag v-else-if="info.enabled && info.card_uuid == card.card_uuid" size="mini" type="success">Autopay</el-tag>
              </div>
              <a class="remove" @click.prevent="removeCard(card)">Remove</a>
            </label>
          </div>
          <span class="addBtn" v-if="!showForm" @click="openForm" v-loading="preparing">+ Add a card</span>
        </div>

        <!-- add a card: the number and CVV are typed into Stax's own fields and go straight to Stax -->
        <div class="section" v-if="showForm">
          <div class="subtitle">Add a card</div>
          <form onsubmit="return false;" v-loading="cardSaving">
            <div class="group">
              <label>
                <span>Card number</span>
                <div id="staxjs-number" class="input-box"></div>
              </label>
              <div class="groupDiv">
                <label>
                  <span>CVV</span>
                  <div id="staxjs-cvv" class="input-box"></div>
                </label>
                <label>
                  <span>Expiration date</span>
                  <el-date-picker v-model="cardForm.expiration" style="width: 100%;" type="month"
                    placeholder="MM/YYYY" format="MM/yyyy" value-format="MM/yyyy" placement="bottom-start">
                  </el-date-picker>
                </label>
              </div>
              <label>
                <span>Name on card</span>
                <div class="names">
                  <input v-model="cardForm.first_name" autocomplete="off" class="input-box" placeholder="First name"/>
                  <input v-model="cardForm.last_name" autocomplete="off" class="input-box" placeholder="Last name"/>
                </div>
              </label>
            </div>
            <p class="hint">Your card number is sent directly to our payment processor (Stax) and is never stored by us.</p>
            <div class="buttons">
              <span class="btn gray" @click="closeForm">Cancel</span>
              <span class="btn" @click="saveCard">Save card</span>
            </div>
          </form>
        </div>

        <!-- turn on / change -->
        <div class="section" v-if="info.allowed && info.cards.length > 0">
          <div class="subtitle">{{ info.enabled ? 'Change autopay' : 'Turn on autopay' }}</div>
          <p v-if="!selectedCard" class="hint">Select a card above.</p>
          <div v-if="info.past_due_invoices.length > 0" class="pastDue">
            <p>These invoices are already past due:</p>
            <ul>
              <li v-for="inv in info.past_due_invoices" :key="inv.client_invoice_uuid">
                {{ inv.invoice_label }} · due {{ date(inv.due_date) }} · ${{ money(inv.amount) }}
              </li>
            </ul>
            <el-checkbox v-model="includePastDue">Also pay these with autopay (total ${{ money(pastDueTotal) }})</el-checkbox>
          </div>
          <div class="consent">
            <el-checkbox v-model="consent"></el-checkbox>
            <span>{{ info.consent_text }}</span>
          </div>
          <div class="buttons">
            <span class="btn" :class="{ disabled: !canSave }" @click="turnOn" v-loading="saving">
              {{ info.enabled ? 'Save' : 'Turn on autopay' }}
            </span>
          </div>
        </div>

        <!-- coming charges -->
        <div class="section" v-if="info.enabled">
          <div class="subtitle">Coming automatic payments</div>
          <el-table :data="info.upcoming_charges" size="small" empty-text="No open invoices">
            <el-table-column prop="invoice_label" label="Invoice"></el-table-column>
            <el-table-column label="Due">
              <template slot-scope="scope">{{ date(scope.row.due_date) }}</template>
            </el-table-column>
            <el-table-column label="Amount">
              <template slot-scope="scope">${{ money(scope.row.amount) }}</template>
            </el-table-column>
            <el-table-column label="Payment">
              <template slot-scope="scope">{{ stateText(scope.row) }}</template>
            </el-table-column>
          </el-table>
        </div>
      </template>
    </div>
  </div>
</template>
<script>
  import { getActionApi, postActionApi, putActionApi, deleteActionApi } from '@/network/manageApi'
  import moment from 'moment/moment'

  // the states of autopay_controller.plan_invoice
  const STATE_TEXT = {
    due: 'Being charged today',
    scheduled: 'Charged on {date}',
    pending: 'Waiting for the card company',
    done: 'Paid',
    needs_review: 'Being checked',
    stopped: 'Declined - please pay it on the Bills page',
    card_declined: 'Card declined - please add another card',
    card_expired: 'Card expired - please add another card',
    not_included: 'Not included (was past due)',
    excluded: 'Not paid automatically',
    no_card: 'No card',
  }

  export default {
    name: 'Autopay',
    data() {
      return {
        loading: false,
        loadError: '',
        info: null,
        selectedCard: null,
        includePastDue: false,
        consent: false,
        saving: false,
        preparing: false,
        showForm: false,
        cardSaving: false,
        staxjs: null,
        formComplete: false,
        formError: 'Please enter the card number and CVV',
        cardForm: { expiration: '', first_name: '', last_name: '' },
      }
    },
    computed: {
      autopayCard() {
        return this.info && this.info.cards.find(card => card.card_uuid == this.info.card_uuid)
      },
      pastDueTotal() {
        return this.info ? this.info.past_due_invoices.reduce((sum, inv) => sum + inv.amount, 0) : 0
      },
      canSave() {
        return !!this.selectedCard && this.consent
      },
    },
    mounted() {
      this.load()
    },
    methods: {
      load() {
        this.loading = true
        return getActionApi('/payment/autopay').then(res => {
          this.info = res.data
          this.loadError = ''
          this.includePastDue = res.data.include_past_due
          const usable = res.data.cards.filter(card => !card.expired)
          if (!usable.find(card => card.card_uuid == this.selectedCard)) {
            this.selectedCard = res.data.card_uuid || (usable.length ? usable[0].card_uuid : null)
          }
        }).catch(err => {
          this.loadError = this.message(err, 'Unable to load autopay. Please refresh the page.')
        }).finally(() => {
          this.loading = false
        })
      },
      message(err, fallback) {
        try {
          return err.response.data.details.data.message || fallback
        } catch (e) {
          return fallback
        }
      },
      // the form is rebuilt each time it opens, with new Stax fields. A company without a Stax customer
      // gets one first (the card is saved under it)
      openForm() {
        const ready = this.info.stax_customer_id
          ? Promise.resolve()
          : postActionApi('/payment/autopay/stax-customer', {}).then(res => {
              this.info.stax_customer_id = res.data.stax_customer_id
              this.info.card_payments_ready = true
            })
        this.preparing = true
        ready.then(() => {
          this.showForm = true
          this.formComplete = false
          this.$nextTick(() => this.initCardForm())
        }).catch(err => {
          this.$message.error(this.message(err, 'Unable to set up card payments. Please try again.'))
        }).finally(() => {
          this.preparing = false
        })
      },
      closeForm() {
        this.showForm = false
        this.staxjs = null
        this.cardForm = { expiration: '', first_name: '', last_name: '' }
      },
      initCardForm() {
        this.staxjs = new StaxJs(process.env.VUE_APP_STAX_TOKEN, {
          number: { id: 'staxjs-number', placeholder: '0000 0000 0000 0000', style: 'height: 30px; width: 100%; font-size: 15px;',
                    type: 'text', format: 'prettyFormat' },
          cvv: { id: 'staxjs-cvv', placeholder: 'CVV', style: 'height: 30px; width: 100%; font-size: 15px;' },
        })
        this.staxjs.showCardForm().catch(err => this.$message.error(String(err)))
        this.staxjs.on('card_form_complete', () => {
          this.formComplete = true
        })
        this.staxjs.on('card_form_incomplete', (message) => {
          this.formComplete = false
          this.formError = !message.validNumber ? 'Please enter the correct card number' : 'Please enter the correct CVV'
        })
      },
      saveCard() {
        if (!this.formComplete) return this.$message.error(this.formError)
        if (!this.cardForm.expiration) return this.$message.error('Please enter the expiration date')
        if (!this.cardForm.first_name || !this.cardForm.last_name) return this.$message.error('Please enter the name on the card')
        const [month, year] = this.cardForm.expiration.split('/')
        this.cardSaving = true
        // saved under the company's own Stax customer; the API checks that again with Stax before keeping it
        this.staxjs.tokenize({
          customer_id: this.info.stax_customer_id,
          firstname: this.cardForm.first_name,
          lastname: this.cardForm.last_name,
          month: month,
          year: year,
          method: 'card',
          validate: false,
        }).then(paymentMethod => {
          return postActionApi('/payment/autopay/cards', { payment_method_id: paymentMethod.id })
        }).then(res => {
          this.$message.success('Card saved')
          this.closeForm()
          this.selectedCard = res.data.card_uuid
          return this.load()
        }).catch(err => {
          if (err && err.response) {
            this.$message.error(this.message(err, 'Unable to save the card'))
          } else {
            this.$message.error((err && err.message) || 'The card could not be saved. Please check it and try again.')
          }
        }).finally(() => {
          this.cardSaving = false
        })
      },
      removeCard(card) {
        const inUse = this.info.enabled && this.info.card_uuid == card.card_uuid
        this.$confirm(inUse ? 'This card is used for autopay. Removing it turns autopay off.' : 'Remove this card?',
          'Remove ' + this.cardName(card), { confirmButtonText: 'Remove', cancelButtonText: 'Cancel', type: 'warning' })
          .then(() => deleteActionApi('/payment/autopay/cards/' + card.card_uuid))
          .then(() => {
            this.$message.success('Card removed')
            return this.load()
          })
          .catch(err => {
            if (err !== 'cancel') this.$message.error(this.message(err, 'Unable to remove the card'))
          })
      },
      turnOn() {
        if (!this.selectedCard) return this.$message.error('Please select a card')
        if (!this.consent) return this.$message.error('Please agree to the autopay terms')
        this.saving = true
        putActionApi('/payment/autopay', {
          enabled: true,
          card_uuid: this.selectedCard,
          consent: true,
          consent_version: this.info.consent_version,
          include_past_due: this.includePastDue,
        }).then(res => {
          this.$message.success('Autopay is on')
          this.consent = false
          return this.load()
        }).catch(err => {
          this.$message.error(this.message(err, 'Unable to turn on autopay'))
          if (err && err.response && err.response.status == 409) this.load()
        }).finally(() => {
          this.saving = false
        })
      },
      turnOff() {
        this.$confirm('Invoices will no longer be charged automatically.', 'Turn off autopay?',
          { confirmButtonText: 'Turn off', cancelButtonText: 'Cancel', type: 'warning' })
          .then(() => {
            this.saving = true
            return putActionApi('/payment/autopay', { enabled: false })
          })
          .then(() => {
            this.$message.success('Autopay is off')
            return this.load()
          })
          .catch(err => {
            if (err !== 'cancel') this.$message.error(this.message(err, 'Unable to turn off autopay'))
          })
          .finally(() => {
            this.saving = false
          })
      },
      cardName(card) {
        return (card.card_type ? card.card_type.toUpperCase() : 'Card') + ' ending in ' + card.last4
      },
      stateText(row) {
        return (STATE_TEXT[row.state] || row.state).replace('{date}', this.date(row.charge_date))
      },
      pad(n) {
        return n == null ? '--' : String(n).padStart(2, '0')
      },
      money(n) {
        return Number(n || 0).toFixed(2)
      },
      date(d) {
        return d ? moment(d).format('MMM D, YYYY') : ''
      },
    },
  }
</script>
<style scoped>
  *{box-sizing: border-box;}
  #home{background-color:#F8F9FA;padding:20px;display: grid;gap: 20px;}
  .div{background-color: #ffffff;padding: 30px;display: grid;gap: 24px;box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1);border-radius: 16px;}
  .title{color:#2D3648;font-size: 20px;font-weight: bold;}
  .subtitle{color:#2D3648;font-size: 16px;font-weight: bold;margin-bottom: 12px;}
  .section{border-top:1px solid #EDF0F7;padding-top: 20px;}
  .status{display: flex;align-items: center;justify-content: space-between;gap: 20px;padding: 16px 20px;border-radius: 10px;}
  .status.on{background-color: #F0FFF4;border:1px solid #9AE6B4;}
  .status.off{background-color: #F7FAFC;border:1px solid #E2E8F0;}
  .status b{font-size: 16px;color:#2D3648;}
  .status p{margin: 4px 0 0 0;font-size: 14px;color:#4A5568;}
  .btn{height:40px;min-width:86px;cursor: pointer;background-color: #5856D6;color:#ffffff;border-radius: 6px;font-size: 14px;display: inline-flex;align-items: center;justify-content: center;padding: 0 16px;flex-shrink: 0;}
  .btn.gray{background-color: #EDF2F7;color:#2D3648;}
  .btn.disabled{opacity: 0.5;}
  .buttons{display: flex;gap: 10px;justify-content: flex-end;margin-top: 16px;}
  .cards{display: grid;grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));gap: 12px;margin-bottom: 12px;}
  .card{display: flex;align-items: center;gap: 12px;padding: 14px 16px;border:1px solid #E2E8F0;border-radius: 10px;cursor: pointer;}
  .card.selected{border-color:#5856D6;box-shadow: 0 0 0 1px #5856D6;}
  .card.expired{opacity: 0.6;}
  .card input{width: 18px;height: 18px;flex-shrink: 0;}
  .cardText{display: flex;flex-direction: column;gap: 2px;width: 100%;font-size: 14px;color:#2D3648;}
  .cardText span{font-size: 12px;color:#718096;}
  .cardText .el-tag{align-self: flex-start;}
  .remove{font-size: 13px;color:#E53E3E;cursor: pointer;flex-shrink: 0;}
  .addBtn{cursor: pointer;background-color: #f7f7f7;font-size: 13px;color: #4A5568;border-radius: 4px;padding:8px 16px;display: inline-block;}
  .group{display: grid;grid-template-columns: 1fr 1fr 1fr;grid-gap: 20px;}
  .groupDiv{display: grid;grid-template-columns: 1fr 1fr;grid-gap: 10px;}
  label{color: #8898AA;line-height: 40px;display: flex;flex-direction: column;font-size: 14px;}
  .input-box{height: 40px;line-height: 1;padding: 3px 10px 0 10px;border: 1px solid #DCDFE6;border-radius: 4px;width: 100%;font-size: 14px;}
  .names{display: flex;gap: 2%;}
  .names input{width: 49%;}
  .hint{font-size: 13px;color:#718096;margin: 10px 0 0 0;}
  .pastDue{background-color: #FFFAF0;border:1px solid #FBD38D;border-radius: 10px;padding: 12px 16px;margin-bottom: 16px;font-size: 14px;color:#2D3648;}
  .pastDue p{margin: 0 0 6px 0;}
  .pastDue ul{margin: 0 0 10px 0;padding-left: 20px;}
  .consent{display: flex;gap: 10px;align-items: flex-start;font-size: 14px;color:#4A5568;line-height: 1.5;}
  @media (max-width: 768px) {
    .div{padding: 20px 12px;}
    .group{grid-template-columns: 1fr;}
    .status{flex-direction: column;align-items: flex-start;}
  }
</style>
